import type { ExperimentConfig } from '@/types';

const experiment9Config: ExperimentConfig = {
  id: 'exp9',
  number: 9,
  name: 'Iron Determination in Water and Soil',
  description: 'Determine iron content in water and soil samples using the 1,10-phenanthroline colorimetric method and spectrophotometry at 510 nm. Extract iron from soil using acid digestion and form the orange-red iron-phenanthroline complex.',
  estimatedTime: '120 minutes',
  learningObjectives: [
    'Extract iron from soil using acid digestion (5 mL concentrated HCl + 5 mL water, simmer 15 minutes)',
    'Weigh ~1 g each of sandy soil and loam soil into separate 250 mL or 400 mL beakers',
    'Apply reduction chemistry: Fe³⁺ → Fe²⁺ using hydroxylamine hydrochloride',
    'Form the orange-red iron-phenanthroline complex [Fe(phen)₃]²⁺ at pH 3.2-3.3',
    'Use Spec 20 spectrophotometry at 510 nm for quantitative iron analysis',
    'Prepare 10 ppm Fe stock (1000 mL) from available 1000 ppm',
    'Prepare calibration standards (0.10, 0.50, 1.0, 2.0, 3.0, 5.0 ppm Fe) in 50 mL or 100 mL volumetric flasks',
    'Add hydroxylamine HCl (0.5 mL for 50 mL flask, 1.0 mL for 100 mL flask)',
    'Add phenanthroline (2.5 mL for 50 mL flask, 5.0 mL for 100 mL flask)',
    'Add sodium acetate (4.0 mL for 50 mL flask, 8.0 mL for 100 mL flask)',
    'Calculate iron content: ppm in solution and mg Fe in dry soil samples',
    'Filter, neutralize to pH 3-5 with NaOH, dilute to 100 mL in volumetric flask',
  ],
  safetyNotes: [
    'Concentrated HCl is corrosive - add acid in the FUME HOOD',
    'Hot acid solutions can cause severe burns - keep watch glass lids on beakers',
    'DO NOT ALLOW acid solution to go dry - add a few mL of water if mix becomes almost dry',
    'Hydroxylamine hydrochloride is an irritant - avoid skin contact',
    'Phenanthroline solution stains - handle carefully',
  ],
  requiredEquipment: [
    'Spec 20 spectrophotometer',
    'Spec 20 cuvette',
    'Analytical balance',
    'Hot plate',
    '250 mL or 400 mL beakers with watch glass covers',
    '100 mL volumetric flasks',
    '50 mL or 100 mL volumetric flasks (for standards)',
    'Volumetric pipettes',
    'Filter funnel and paper',
    '100 mL beakers (for filtrate)',
    'Concentrated HCl',
    'Sodium hydroxide (for neutralization to pH 3-5)',
    'Hydroxylamine hydrochloride solution (0.5 mL per 50 mL flask, 1.0 mL per 100 mL flask)',
    'Sodium acetate solution (4.0 mL per 50 mL flask, 8.0 mL per 100 mL flask)',
    'Phenanthroline solution (2.5 mL per 50 mL flask, 5.0 mL per 100 mL flask)',
    '1000 ppm Fe standard solution',
    'Unknown water sample',
  ],
  parts: [
    {
      id: 0,
      name: 'summary',
      title: 'Summary',
      steps: [],
      equipment: [],
      initialState: {},
    },
    {
      id: 1,
      name: 'extraction',
      title: 'Part I: Soil Extraction',
      equipment: [
        'Analytical balance',
        'Hot plate',
        'Beaker (250 mL)',
        'Filter funnel and paper',
        'Volumetric flask (100 mL)',
        'Watch glass',
        'Timer'
      ],
      initialState: {
        soilMass: null,
        balance: {
          reading: 0,
          isTared: false,
          isStable: true
        },
        extraction: {
          acidAdded: false,
          isBoiling: false,
          extractionProgress: 0,
          extractionComplete: false,
          timer: 900
        },
        filtration: {
          isComplete: false,
          filtrateColor: '#D4A574'
        },
        dilution: {
          isComplete: false
        }
      },
      steps: [
        {
          id: 1,
          title: 'Weigh Soil Sample',
          shortDescription: 'Weigh about 1 g each of a sandy soil and a loam soil into separate 250 mL or 400 mL beakers',
          handler: 'handleWeighSoil',
          validation: {
            requires: ['balance.isTared'],
            condition: 'soilMass >= 0.9 && soilMass <= 1.1'
          }
        },
        {
          id: 2,
          title: 'Transfer to Beaker',
          shortDescription: 'Transfer the soil sample to a 250 mL beaker',
          handler: 'handleTransferSoil',
          validation: {
            requires: ['soilMass']
          }
        },
        {
          id: 3,
          title: 'Add Concentrated HCl and Water',
          shortDescription: 'In the FUME HOOD, add about 5 mL of concentrated HCl and 5 mL of water',
          handler: 'handleAddHCl',
          infoContent: {
            title: 'Iron Extraction',
            content: 'In the **FUME HOOD**, add about **5 mL of concentrated HCl** and **5 mL of water** to each beaker. Keep **watch glass lids** on the beakers and swirl the contents occasionally. Hydrochloric acid dissolves iron oxides and hydroxides in the soil.',
            chemicalEquation: 'Fe₂O₃(s) + 6HCl(aq) → 2FeCl₃(aq) + 3H₂O(l)',
            whatHappens: 'Concentrated HCl dissolves iron oxides and hydroxides in the soil, converting insoluble iron compounds to soluble FeCl3 in aqueous solution.',
            observe: [
              'Add the acid in the FUME HOOD — concentrated HCl fumes are corrosive',
              'Keep watch glass lids on the beakers to contain fumes and prevent splashing',
              'The soil-acid mixture may turn yellow-brown as iron dissolves',
            ],
          }
        },
        {
          id: 4,
          title: 'Simmer Gently',
          shortDescription: 'Simmer gently on a hot plate for 15 minutes — DO NOT ALLOW TO GO DRY',
          handler: 'handleBoilExtraction',
          validation: {
            requires: ['extraction.acidAdded']
          },
          infoContent: {
            title: 'Extraction Time (15 Minutes)',
            content: 'Simmer gently on a hot plate for **15 minutes**. **DO NOT ALLOW TO GO DRY** — add a few mL of water if the mix becomes almost dry. Keep **watch glass lids** on the beakers and swirl the contents occasionally. Work on Part B while this is simmering.',
            whatHappens: 'Gentle simmering for 15 minutes extracts iron from soil minerals; the heat accelerates the dissolution of iron oxides and hydroxides by HCl.',
            observe: [
              'Simmer gently — do not boil vigorously',
              'CRITICAL: Do not allow the mixture to go dry — add water if needed',
              'Swirl occasionally to promote contact between acid and soil particles',
              'The solution darkens as iron dissolves into the acid',
            ],
          }
        },
        {
          id: 5,
          title: 'Cool, Add Water, and Filter',
          shortDescription: 'After 15 min, remove from heat, cool, add ~20 mL water, filter to remove solids into 100 mL beaker',
          handler: 'handleFilterExtract',
          validation: {
            requires: ['extraction.extractionComplete']
          }
        },
        {
          id: 6,
          title: 'Neutralize and Dilute to Volume',
          shortDescription: 'Neutralize filtrate to pH 3-5 with NaOH, filter again if necessary, dilute to 100 mL in volumetric flask',
          handler: 'handleDiluteExtract',
          validation: {
            requires: ['filtration.isComplete']
          }
        }
      ]
    },
    {
      id: 2,
      name: 'colorDevelopment',
      title: 'Part II: Color Development',
      equipment: [
        'Volumetric flasks (100 mL)',
        'Volumetric pipettes',
        'Dropper bottles',
        'Timer'
      ],
      initialState: {
        aliquotVolume: null,
        reductant: {
          added: false
        },
        buffer: {
          added: false
        },
        phenanthroline: {
          added: false,
          colorDeveloped: false,
          developmentTime: 600
        },
        solutionColor: '#FFFFFF'
      },
      steps: [
        {
          id: 1,
          title: 'Pipette Aliquot',
          shortDescription: 'Pipet 25 mL of each unknown (soil and water) into volumetric flasks (50 mL aliquot for 100 mL flasks)',
          handler: 'handlePipetteAliquot'
        },
        {
          id: 2,
          title: 'Add Hydroxylamine HCl',
          shortDescription: 'Add 0.5 mL (10 drops) hydroxylamine HCl to each 50 mL flask (1.0 mL for 100 mL flask) to reduce Fe³⁺ → Fe²⁺',
          handler: 'handleAddHydroxylamine',
          infoContent: {
            title: 'Why Reduce Fe3+?',
            content: 'Fe3+ forms a weak, blue complex with phenanthroline. Fe2+ forms the stable, intense orange-red [Fe(phen)₃]2+ complex. Hydroxylamine reduces all iron to the +2 state.',
            chemicalEquation: '2Fe³⁺ + 2NH₂OH → 2Fe²⁺ + N₂O + H₂O + 4H⁺',
            whatHappens: 'Hydroxylamine hydrochloride reduces all Fe3+ to Fe2+, which is required because only Fe2+ forms the stable, intensely coloured complex with phenanthroline.',
            observe: [
              'Add 0.5 mL (10 drops) for 50 mL flask or 1.0 mL for 100 mL flask',
              'The solution may lighten slightly as Fe3+ (yellow) is reduced to Fe2+ (nearly colourless)',
              'Mix thoroughly to ensure complete reduction of all iron',
            ],
          }
        },
        {
          id: 3,
          title: 'Add Sodium Acetate Buffer',
          shortDescription: 'Add 4.0 mL sodium acetate solution (8.0 mL for 100 mL flask)',
          handler: 'handleAddBuffer',
          validation: {
            requires: ['reductant.added']
          },
          infoContent: {
            title: 'pH Control',
            content: 'The iron-phenanthroline complex forms optimally at pH 3.5-4.5. Acetate buffer maintains this pH range.',
            whatHappens: 'Sodium acetate buffer adjusts and maintains the pH at 3.5-4.5, the optimal range for formation of the stable [Fe(phen)3]2+ complex.',
            observe: [
              'Add 4.0 mL sodium acetate for 50 mL flask (8.0 mL for 100 mL flask)',
              'The solution should not change colour significantly at this stage',
              'Proper pH control ensures reproducible complex formation',
            ],
          }
        },
        {
          id: 4,
          title: 'Add 0.1% 1,10-Phenanthroline',
          shortDescription: 'Add 2.5 mL phenanthroline solution (5.0 mL for 100 mL flask)',
          handler: 'handleAddPhenanthroline',
          validation: {
            requires: ['buffer.added']
          },
          infoContent: {
            title: 'The Iron-Phenanthroline Complex',
            content: 'Three phenanthroline molecules wrap around one Fe2+ ion, forming a very stable octahedral complex. This "ferroin" complex is so stable that it is used as a redox indicator.',
            chemicalEquation: 'Fe²⁺ + 3(1,10-phenanthroline) → [Fe(phen)₃]²⁺',
            whatHappens: 'Three phenanthroline molecules chelate one Fe2+ ion to form the intensely orange-red [Fe(phen)3]2+ complex (ferroin), which has a very high molar absorptivity at 510 nm.',
            observe: [
              'Add 2.5 mL phenanthroline for 50 mL flask (5.0 mL for 100 mL flask)',
              'An orange-red colour develops as the ferroin complex forms',
              'Higher iron concentrations produce a more intense orange-red colour',
            ],
          }
        },
        {
          id: 5,
          title: 'Wait for Color Development',
          shortDescription: 'Wait 10 minutes for full color development',
          handler: 'handleWaitColorDevelopment',
          validation: {
            requires: ['phenanthroline.added']
          }
        },
        {
          id: 6,
          title: 'Dilute to Volume',
          shortDescription: 'Swirl to mix contents, then dilute to the mark with distilled water',
          handler: 'handleDiluteToMark',
          validation: {
            requires: ['phenanthroline.colorDeveloped']
          }
        }
      ]
    },
    {
      id: 3,
      name: 'measurement',
      title: 'Part III: Spectrophotometric Measurement',
      equipment: [
        'Spec 20 spectrophotometer',
        'Spec 20 cuvette',
      ],
      initialState: {
        spectrophotometer: {
          isOn: true,
          wavelength: 510,
          isZeroed: false,
          currentReading: null
        },
        cuvettes: {
          blank: { filled: false, absorbance: null },
          std1: { filled: false, absorbance: null, concentration: 0.10 },
          std2: { filled: false, absorbance: null, concentration: 0.50 },
          std3: { filled: false, absorbance: null, concentration: 1.0 },
          std4: { filled: false, absorbance: null, concentration: 2.0 },
          std5: { filled: false, absorbance: null, concentration: 3.0 },
          std6: { filled: false, absorbance: null, concentration: 5.0 },
          sandySoilSample: { filled: false, absorbance: null },
          loamSoilSample: { filled: false, absorbance: null },
          waterSample: { filled: false, absorbance: null }
        },
        calibration: {
          points: [],
          slope: null,
          intercept: null,
          r2: null
        },
        result: {
          ppmInWater: null,
          mgFeSandySoil: null,
          mgFeLoamSoil: null
        }
      },
      steps: [
        {
          id: 1,
          title: 'Set Wavelength',
          shortDescription: 'Set spectrophotometer to 510 nm',
          handler: 'handleSetWavelength',
          infoContent: {
            title: 'Beer-Lambert at 510 nm',
            content: 'The molar absorptivity (ε) of [Fe(phen)₃]²⁺ is 11,100 L/mol·cm at 510 nm - very high! This gives excellent sensitivity for iron detection at ppm levels.',
            whatHappens: 'The spectrophotometer is set to 510 nm, the absorption maximum of the ferroin complex, where its very high molar absorptivity (11,100 L/mol.cm) provides excellent detection sensitivity.',
            observe: [
              'Set the wavelength dial precisely to 510 nm',
              'The orange-red complex absorbs green light (510 nm) and transmits red-orange',
              'The high molar absorptivity allows detection of iron at sub-ppm levels',
            ],
          }
        },
        {
          id: 2,
          title: 'Zero with Blank',
          shortDescription: 'Insert reagent blank and zero the spectrophotometer',
          handler: 'handleZeroSpec'
        },
        {
          id: 3,
          title: 'Measure Standards',
          shortDescription: 'Measure absorbance of iron standard solutions (0.10, 0.50, 1.0, 2.0, 3.0, 5.0 ppm)',
          handler: 'handleMeasureStandards',
          validation: {
            requires: ['spectrophotometer.isZeroed']
          }
        },
        {
          id: 4,
          title: 'Plot Calibration Curve',
          shortDescription: 'Plot A vs [Fe] and perform linear regression',
          handler: 'handlePlotCalibration'
        },
        {
          id: 5,
          title: 'Measure Sample',
          shortDescription: 'Measure the absorbance of the soil extract samples (sandy and loam) and unknown water sample',
          handler: 'handleMeasureSample'
        },
        {
          id: 6,
          title: 'Calculate Iron Content',
          shortDescription: 'Calculate iron content in unknown water and soil samples. Determine soil concentration as mg in the dry sample.',
          handler: 'handleCalculateIron',
          validation: {
            requires: ['calibration.slope']
          },
          infoContent: {
            title: 'Iron Content Calculation',
            content: 'Using the equation of the line from the calibration graph, calculate the concentration of the unknown water and soil samples. Determine the concentration in the soil samples as **mg Fe in the dry sample**. If a sample gives a stronger absorbance than the strongest standard, a serial dilution must be done until its absorbance fits within the range of the standards. Maximum acceptable iron in drinking water is **0.3 mg/L**.',
            whatHappens: 'Sample absorbance values are substituted into the calibration equation to determine iron concentration in ppm for water and mg Fe per gram for soil samples.',
            observe: [
              'Use the calibration equation to convert absorbance to concentration (ppm)',
              'For soil: convert ppm in the extract to mg Fe in the original dry sample',
              'If any sample absorbance exceeds the highest standard, dilute and re-measure',
              'Compare water sample results to the 0.3 mg/L drinking water standard',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp9',
    title: 'Iron Determination Quiz',
    questions: [
      {
        id: 'exp9-q1',
        type: 'mcq' as const,
        question: 'What wavelength is used for spectrophotometric measurement of the iron-phenanthroline complex?',
        options: [
          { id: 'a', text: '400 nm' },
          { id: 'b', text: '450 nm' },
          { id: 'c', text: '510 nm' },
          { id: 'd', text: '580 nm' },
        ],
        correctOptionId: 'c',
        explanation: 'The orange-red [Fe(phen)₃]\u00B2⁺ complex absorbs at 510 nm.',
      },
      {
        id: 'exp9-q2',
        type: 'mcq' as const,
        question: 'What is the role of hydroxylamine hydrochloride in this experiment?',
        options: [
          { id: 'a', text: 'It acts as a pH buffer' },
          { id: 'b', text: 'It reduces Fe\u00B3⁺ to Fe\u00B2⁺' },
          { id: 'c', text: 'It forms the colored complex with iron' },
          { id: 'd', text: 'It dissolves the soil sample' },
        ],
        correctOptionId: 'b',
        explanation: 'Hydroxylamine hydrochloride is a reducing agent that converts Fe\u00B3⁺ (ferric) to Fe\u00B2⁺ (ferrous), which is required to form the colored complex with 1,10-phenanthroline.',
      },
      {
        id: 'exp9-q3',
        type: 'short_answer' as const,
        question: 'Why must the soil extract be neutralized to pH 3-5 with NaOH before analysis?',
        sampleAnswer: 'The iron-phenanthroline complex [Fe(phen)₃]\u00B2⁺ forms optimally at pH 3.2-3.3. The acid digestion uses concentrated HCl, which makes the solution too acidic. Neutralizing to pH 3-5 with NaOH ensures the proper pH range for complex formation and color development.',
      },
      {
        id: 'exp9-q4',
        type: 'calculation' as const,
        question: 'To prepare a 10 ppm Fe stock solution (1000 mL) from a 1000 ppm standard, how many mL of the 1000 ppm standard are needed?',
        expectedValue: 10,
        tolerance: 5,
        unit: 'mL',
        explanation: 'C₁V₁ = C₂V₂: 1000 ppm × V₁ = 10 ppm × 1000 mL, so V₁ = 10 mL',
      },
      {
        id: 'exp9-q5',
        type: 'mcq' as const,
        question: 'What is the role of sodium acetate in this experiment?',
        options: [
          { id: 'a', text: 'It acts as a reducing agent' },
          { id: 'b', text: 'It acts as a buffer to maintain pH 3.2-3.3' },
          { id: 'c', text: 'It forms a complex with iron' },
          { id: 'd', text: 'It precipitates impurities' },
        ],
        correctOptionId: 'b',
        explanation: 'Sodium acetate acts as a buffer to maintain the solution at pH 3.2-3.3, which is optimal for the iron-phenanthroline complex formation.',
      },
    ],
  },
};

export default experiment9Config;
